import { parishToSlug } from '../constants/jamaicaParishes';
import {
  KINGSTON_POSTAL_DISTRICTS,
  KINGSTON_POSTAL_LOOKUP_VERSION,
  resolveKingstonPostal,
  kingstonPostalEnabled,
  kingstonPostalHref,
  isKingstonPostalArea,
} from './kingstonPostal.mjs';

export const postalSearchEnabled = () => {
  if (!kingstonPostalEnabled(process.env.REACT_APP_KINGSTON_POSTAL_ENABLED)) return false;
  const marker = typeof document !== 'undefined'
    ? document.querySelector('#recs-legacy-page[data-kingston-postal-enabled], #recs-catalogue[data-kingston-postal-enabled]')
    : null;
  return !marker || kingstonPostalEnabled(marker.dataset.kingstonPostalEnabled);
};
export function postalSearchError(message = 'Kingston postal district search is temporarily unavailable.') {
  const error = new Error(message);
  error.status = 503;
  return error;
}

export function postalRoute(pathname) {
  const parts = String(pathname || '').split('/').filter(Boolean);
  if (parts[0] !== 'homes' || !['for-sale', 'for-rent', 'commercial'].includes(parts[1])) return null;
  const view = ['map', 'split'].includes(parts[2]) ? parts.splice(2, 1)[0] : '';
  const area = parts.slice(2);
  if (!isKingstonPostalArea(area)) return null;
  return { district: resolveKingstonPostal(area[2]), value: area[2], view, type: parts[1], category: area[3] || '' };
}

export function postalFilters(filters, value, { clearSearch = true } = {}) {
  const district = resolveKingstonPostal(typeof value === 'object' ? value?.slug : value);
  if (!district) throw postalSearchError('This Kingston postal district is unavailable.');
  // Postal membership defines geography. No parish, textual geocoder, or
  // proximity search can silently widen it. An explicit map viewport can refine it.
  return { ...filters, postal_district: district.slug, country: 'Jamaica', region: null, city: null,
    lat: null, lng: null, search: clearSearch ? '' : (filters.search || ''), bounds: null, radius: null, address: null };
}

export function postalInput(value) {
  const text = String(value || '').trim().replace(/,?\s*jamaica$/i, '').trim();
  const district = resolveKingstonPostal(text);
  return district || /^(?:kingston|kgn)[\s-]*\d+$/i.test(text) ? { district } : null;
}

export function postalSuggestions(input) {
  const query = String(input || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (query.length < 2) return [];
  const exact = postalInput(input)?.district;
  const matches = exact ? [exact] : KINGSTON_POSTAL_DISTRICTS.filter((district) =>
    [district.label, ...district.aliases].some((alias) => alias.toLowerCase().replace(/[^a-z0-9]/g, '').startsWith(query)));
  return matches.map((district) => ({ id: `postal-${district.slug}`, section: 'postal', postal_district: district.slug,
    formatted: `${district.label}, Jamaica`, primaryLine: district.label, secondaryLine: 'Kingston postal district · Jamaica' }));
}

export function postalSearchPath(value, filters = {}, pathname = '/') {
  const route = postalRoute(pathname);
  const parts = pathname.split('/').filter(Boolean);
  const view = route?.view || (['map', 'split'].includes(parts[2]) ? parts[2] : parts[0] === 'homes' ? '' : 'split');
  const category = filters.categories?.length === 1 ? filters.categories[0] : '';
  return kingstonPostalHref(value, filters.type || route?.type || (['for-sale', 'for-rent', 'commercial'].includes(parts[1]) ? parts[1] : 'sale'), { view, category });
}


export const postalRouteKey = (pathname, search = '') => JSON.stringify([pathname, search]);
// On navigation the URL owns the first request. Later drawer changes can use
// context once the route has been stored, without retaining another page's filters.
export function postalRouteFilters(pathname, search = '', filters = {}) {
  const route = postalRoute(pathname);
  if (!route?.district) throw postalSearchError();
  const key = postalRouteKey(pathname, search);
  if (filters.__postalRoute === key) return postalFilters(filters, route.district, { clearSearch: false });
  const result = { type: route.type === 'commercial' ? 'commercial' : route.type.replace('for-', ''),
    hide_under_offer: true, categories: [], features: [], search: '', __postalRoute: key };
  const query = new URLSearchParams(search);
  const allowed = ['search', 'price_min', 'price_max', 'currency', 'bedrooms', 'bathrooms', 'age_min', 'age_max',
    'parking_spaces', 'sqft_min', 'sqft_max', 'lot_min', 'lot_max', 'basement', 'garage', 'listed_within', 'hide_under_offer'];
  for (const key of allowed) {
    if (!['search', 'hide_under_offer'].includes(key)) result[key] = null;
    if (query.has(key)) result[key] = key === 'hide_under_offer' ? query.get(key) !== 'false' : query.get(key);
  }
  for (const key of ['categories', 'features']) result[key] = [...new Set([
    ...query.getAll(key + '[]'), ...(query.get(key)?.split(',') || []),
    ...(key === 'categories' && route.category ? [route.category] : []),
  ].filter(Boolean))];
  return postalFilters(result, route.district, { clearSearch: false });
}


export function ordinarySearchPath(filters = {}, pathname = '/') {
  const parts = String(pathname).split('/').filter(Boolean);
  const type = filters.type || (['for-sale', 'for-rent', 'commercial'].includes(parts[1]) ? parts[1] : 'for-sale');
  const transaction = type === 'commercial' ? type : type.startsWith('for-') ? type : `for-${type}`;
  const view = ['map', 'split'].includes(parts[2]) ? parts[2] : '';
  const slug = (value) => String(value || '').toLowerCase().replace(/[.'’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const region = filters.region ? parishToSlug(filters.region) || slug(filters.region) : '';
  const area = [slug(filters.country || 'Jamaica'), region, filters.city ? slug(filters.city) : '',
    filters.categories?.length === 1 ? filters.categories[0] : ''].filter(Boolean);
  return `/homes/${transaction}/${view ? view + '/' : ''}${area.join('/')}`;
}

const metadata = new WeakMap();
export function getPostalReadiness(api) {
  if (!postalSearchEnabled()) return Promise.reject(postalSearchError());
  const cached = metadata.get(api);
  if (cached && cached.expires > Date.now()) return cached.promise;
  const promise = api.get('/properties/postal-districts').then(({ data }) => {
    if (data?.ready !== true || data?.postalLookupVersion !== KINGSTON_POSTAL_LOOKUP_VERSION) throw postalSearchError();
    return data;
  }).catch((error) => { metadata.delete(api); throw error; });
  metadata.set(api, { promise, expires: Date.now() + 60000 });
  return promise;
}

export async function preparePostalRequest(api, params = {}) {
  if (params.postal_district === null || params.postal_district === undefined || params.postal_district === '') return params;
  const result = postalFilters(params, params.postal_district, { clearSearch: false });
  delete result.__postalRoute;
  await getPostalReadiness(api);
  return result;
}
